import { de, enUS, Locale } from 'date-fns/locale';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export enum Language {
    ENGLISH = 'ENGLISH',
    GERMAN = 'GERMAN',
}

export const getDateLocaleByLocale = (locale: 'en' | 'de' | Language) => {
    const locales: Record<'en' | 'de' | Language, Locale> = {
        'en': enUS,
        'ENGLISH': enUS,

        'de': de,
        'GERMAN': de,
    };
    return locales[locale];
}

const translations = {
    [Language.ENGLISH]: {
        locale: 'en',

        monday: 'Monday',
        mondayShort: 'Mon',
        
        tuesday: 'Tuesday',
        tuesdayShort: 'Tue',
        
        wednesday: 'Wednesday',
        wednesdayShort: 'Wed',
        
        thursday: 'Thursday',
        thursdayShort: 'Thu',
        
        friday: 'Friday',
        fridayShort: 'Fri',
        
        saturday: 'Saturday',
        saturdayShort: 'Sat',
        
        sunday: 'Sunday',
        sundayShort: 'Sun',
        
        continue: 'Continue',

        location: 'Location',
        clients: 'clients',
        ourDoctors: 'Our doctors',
        reviews: 'Reviews',
        contact: 'Contact',
        address: 'Address',
        phone: 'Phone',
        email: 'Email',
        closed: 'Closed',
        openingHours: 'Opening Hours',
        seeAvailableAppointments: 'See available appointments',
        selectClinic: 'Select clinic',
        selectStoreButton: 'Select',
        changeStoreButton: 'Change',

        services: 'services',
        treatments: 'treatments',
        searchTreatments: 'Search treatments',
        addTreatmentsToContinue: 'Add treatments to continue',
        bookTreatments: 'Book treatments',
        totalPrice: 'Total price',
        total: 'Total',
        free: 'Free',
        startingAt: 'Starting at',
        add: 'Add',

        timeAndDate: 'Time and date',
        freeSlots: 'free',
        noSlots: 'No slots',
        availableAppointments: 'Available appointments',
        morning: 'Morning',
        afternoon: 'Afternoon',
        evening: 'Evening',
        oClock: '',
        selectATimeslotToContinue: 'Select a timeslot to continue',
        showMore: 'Show more',
        showLess: 'Show less',
        more: 'more',
        noAppointmentsAvailableJoinWaitlist: 'No appointments available on this day. Would you like to join the waitlist?',
        noAppointmentsPleaseSelectOtherTime: "No appointments available at this time. Please select a different time.",
        joinWaitlistFor: 'Join waitlist for', // <date> comes right after this, with a space in between
        wantAnEarlierAppointment: 'Looking for an earlier time?',
        putYourselfOnWaitlist: 'Put yourself on our waitlist.', // Link to put on waitlist will be for this text.
        waitlistSignupSuccess: "You have successfully been added to our waitlist.",
        waitlistSignupError: "Something went wrong when signing up for our waitlist. Please try again.",
        preferredContactTimesLabel: 'Preferred contact times (optional)',
        contactTimeMorning: "Morning (08:00 - 12:00)",
        contactTimeAfternoon: "Afternoon (12:00 - 16:00)",
        contactTimeEvening: "Abend (16:00 - 22:00)",
        submitWaitlistButton: "Submit",
        waitlistFormTitle: "Waitlist - Stay In Touch",
        waitlistFormSubtitle: "We will notify you by SMS and E-Mail if an earlier time becomes available for your selected service.",

        personalDetails: 'Personal details',
        
        firstNameLabel: 'First name',
        enterFirstNamePlaceholder: 'Enter first name',
        firstNameRequired: 'First name is required',
        firstNameHelpMessage: 'Please enter a valid name (2-50 characters)',
        
        lastNameLabel: 'Last name',
        enterLastNamePlaceholder: 'Enter last name',
        lastNameRequired: 'Last name is required',
        lastNameHelpMessage: 'Please enter a valid name (2-50 characters)',
        
        emailAddressLabel: 'Email address',
        enterEmailAddressPlaceholder: 'Enter email address',
        emailAddressRequired: 'Email is required',
        emailAddressHelpMessage: 'Please enter a valid email address',
        
        phoneNumberLabel: 'Phone number',
        enterPhoneNumberPlaceholder: 'Enter your number',
        phoneNumberRequired: 'Phone number is required',
        phoneNumberHelpMessage: 'Please enter a valid phone number',
        
        wantEarlierTimeslotLabel: 'I would like an earlier appointment if one becomes available',
        
        messageLabel: 'Message (optional)',
        enterMessagePlaceholder: 'Leave a message or ask anything...',
        // Here, {AGB's|AGB} will give you "AGB's" text with AGB link
        // and {Datenschutzbestimmungen|DATENSCHUTZ} will give you "Datenschutzbestimmungen" text with the DATENSCHUTZ link.
        iAcceptTermsAndConditions: "I accept the {terms & conditions|AGB} and {privacy policy|DATENSCHUTZ}.*",
        reviewBooking: 'Review booking',
        fillOutYourInformationToContinue: 'Fill out your information to continue.',

        yourTreatments: 'Your treatments',
        emptyCart: 'Your cart is empty',
        addTreatmentsToGetStarted: 'Add treatments to get started',

        treatmentPackagePopupTitle: 'This treatment is available as a package',
        treatmentPackagePopupDescription: 'This package deal is cheaper than the stand-alone treatment and supports a more succesful treatment result.',
        inPackage: 'in a package',
        perTreatment: 'per Treatment',
        treatmentPackageSave: 'Save',
        toastAddedItemToCart: 'Successfully added package upgrade to your cart!',
        upgradeToPackageDealButton: 'Book Package',
        skipUpgradeToPackageDealButton: 'Skip',

        appointmentReview: 'Appointment review',
        confirmBooking: 'Confirm booking',
        discountCodeLabel: 'Discount code',
        discountCodePlaceholder: 'Enter discount code',
        discountCodeCaption: 'This will be applied in-person at the clinic.',
        bookingErrorTheDesiredSlotIsNotAvailableAnymore: 'The desired slot is not available anymore. Please choose a different timeslot.',
        bookingErrorUnknown: 'The desired slot could not be booked. Please choose a different time slot.',
        bookingErrorInvalidSelectedData: 'The booking could not be made because no timeslot was selected. Please go back and select a timeslot.',
        bookingErrorEmailBlocked: 'You are not able to book online. Please contact the clinic in order to schedule an appointment directly.',

        bookingConfirmedTitle: 'Thank you for booking!',
        bookingConfirmedInstructions: 'Your appointment has been successfully booked. You will receive a confirmation email shortly.',

        backToBeginning: 'Back to beginning',

        // Login
        loginTitle: 'Welcome Back',
        loginSubtitle: 'Enter your email to receive a login link',
        loginEmailPlaceholder: 'Enter your email address',
        loginSendMagicLink: 'Send Login Link',
        loginSendingMagicLink: 'Sending...',
        loginMagicLinkSent: 'Check your email! We sent you a login link.',
        loginMagicLinkError: 'No account found with this email address.',
        loginGenericError: 'Something went wrong. Please try again.',
        loginBackToBooking: 'Back to booking',
        loginVerifying: 'Verifying your login...',
        loginVerificationFailed: 'Login verification failed. The link may be expired.',
        loginTryAgain: 'Try again',
        loginPromptText: 'Been here before?',
        loginPromptLink: 'Log in',
        loggedInAs: 'Logged in as',
        logout: 'Log out',

        // Treatment Plan Card
        yourTreatmentPlan: 'Your Treatment Plan',
        viewYourPersonalizedPlan: 'View your personalized skin journey',

        loadingYourTimeslot: 'Loading your timeslot',
        pleaseWaitWhileWeLoadYourAvailableTimeslots: 'Please wait while we load your available timeslots',
        timeslotNoLongerAvailable: 'Timeslot no longer available',
        timeslotAlreadyTakenByOtherCustomer: 'This timeslot has already been taken by another customer.',
        checkForNewTimeslots: 'Check for new timeslots',
        appointmentConfirmed: 'Appointment confirmed!',
        yourAppointmentHasBeenSuccessfullyBooked: 'Your appointment has been successfully booked.',
        service: 'Service',
        confirmationEmailSentTo: 'A confirmation email has been sent to',
        claimYourTimeslot: 'Claim your timeslot',
        greatNewsTimeslotAvailable: 'Great news! A timeslot has become available.',
        bookedFor: 'Booked for',
        availableTimeslots: 'Available timeslots',
        discountCode: 'Discount code',
        optional: 'optional',
        enterDiscountCode: 'Enter discount code',
        messageToSalon: 'Message to salon',
        anySpecialRequests: 'Any special requests or questions?',
        bookAppointment: 'Book appointment',
        byBookingYouConfirmAvailability: 'By booking, you confirm this timeslot is still available.',
        waitlistClaimAllContactFieldsRequired: 'All fields below are required to book.',
        fieldRequiredShort: '(required)',
        waitlistWithdrawNotificationsLabel: 'Stop receiving notifications for this waitlist entry',
        waitlistWithdrawConfirmTitle: 'Withdraw from waitlist?',
        waitlistWithdrawConfirmDescription:
            'You will no longer receive emails about available slots for this request. This cannot be undone.',
        waitlistWithdrawConfirmButton: 'Yes, withdraw',
        waitlistWithdrawCancelButton: 'Cancel',
        waitlistWithdrawSuccess: 'You will no longer receive notifications for this waitlist entry.',
        waitlistWithdrawError: 'Could not withdraw. Please check your email or try again.',
        waitlistEntryNotAvailable: 'This waitlist link is invalid or no longer active.',
        waitlistWithdrawnTitle: 'Notifications stopped',
        waitlistWithdrawnDescription: 'You will no longer receive messages about this waitlist entry.',
        waitlistLoginRequired: 'Login required',
        waitlistLoginDescription: 'Please log in to view your available timeslots. We will send you a link via email.',
        waitlistSendLoginLink: 'Send login link',
        waitlistLoginLinkSent: 'A login link has been sent to your email. Please check your inbox.',
        waitlistLoginLinkError: 'Could not send login link. Please try again.',
        appointmentBookedSuccessfully: 'Appointment booked successfully!',
        appointmentBookingFailed: 'Appointment booking failed. The timeslot may no longer be available.',
        waitlistDuplicateAppointmentTitle: 'You already have this appointment booked',
        waitlistDuplicateAppointmentWouldMoveLabel: 'Appointment that would be moved',
        waitlistDuplicateAppointmentNewTimeLabel: 'New time',
        waitlistDuplicateAppointmentDescription:
            'Choose whether to move that appointment to the waitlist time you selected, or keep it and book an additional appointment.',
        waitlistRescheduleExistingButton: 'Move my existing appointment',
        waitlistBookNewAppointmentButton: 'Book a new appointment',
        clinic: 'Clinic',


        preferredPractitionerPlaceholder: 'Choose your practitioner',
        preferredPractitionerNoPreference: 'No preference',
        preferredPractitionerInfoPast: 'You can choose from all practitioners who have treated you before.',
        preferredPractitionerInfoCombined: 'For combined appointments with medical and cosmetic services, you can currently only choose the practitioner for the medical service.',

        deeplinkNotFound: "Link Not Found",
        deeplinkNotFoundDescription: 'The booking link you followed is not valid or has expired.',
        deeplinkLocationAndServiceContinueToBookingButton: 'Continue to booking',
    },
    [Language.GERMAN]: {
        locale: 'de',

        monday: 'Montag',
        mondayShort: 'Mo',
        
        tuesday: 'Dienstag',
        tuesdayShort: 'Di',
        
        wednesday: 'Mittwoch',
        wednesdayShort: 'Mi',
        
        thursday: 'Donnerstag',
        thursdayShort: 'Do',
        
        friday: 'Freitag',
        fridayShort: 'Fr',
        
        saturday: 'Samstag',
        saturdayShort: 'Sa',
        
        sunday: 'Sonntag',
        sundayShort: 'So',
        
        continue: 'Weiter',

        location: 'Standort',
        clients: 'Kunden',
        ourDoctors: 'Unsere Ärzte',
        reviews: 'Bewertungen',
        contact: 'Kontakt',
        address: 'Adresse',
        phone: 'Telefon',
        email: 'Email',
        openingHours: 'Öffnungszeiten',
        closed: 'Geschlossen',
        seeAvailableAppointments: 'Termine anzeigen',
        selectClinic: 'Klinik auswählen',
        selectStoreButton: 'Auswählen',
        changeStoreButton: 'Ändern',
        
        services: 'Leistungen',
        treatments: 'Behandlungen',
        searchTreatments: 'Behandlung suchen',
        addTreatmentsToContinue: 'Behandlung hinzufügen',
        bookTreatments: 'Behandlungen buchen',
        totalPrice: 'Gesamtpreis',
        total: 'Gesamt',
        free: 'Kostenlos',
        startingAt: 'Ab',
        add: 'Hinzufügen',

        timeAndDate: 'Zeit und Datum',
        freeSlots: 'frei',
        noSlots: 'Keine Termine',
        availableAppointments: 'Verfügbare Termine',
        morning: 'Vormittag',
        afternoon: 'Nachmittag',
        evening: 'Abend',
        oClock: 'Uhr',
        selectATimeslotToContinue: 'Termin auswählen',
        showMore: 'Mehr anzeigen',
        showLess: 'Weniger anzeigen',
        more: 'mehr',
        noAppointmentsAvailableJoinWaitlist: 'Keine Termine verfügbar. Möchtest Du dich in die Warteliste eintragen?',
        noAppointmentsPleaseSelectOtherTime: "Keine Termine verfügbar. Bitte wählen Sie eine andere Zeit.",
        joinWaitlistFor: 'Warteliste für', // <date> comes right after this, with a space in between
        wantAnEarlierAppointment: 'Du hast Interesse an einem früheren Termin?',
        putYourselfOnWaitlist: 'Dann setze Dich auf unsere Warteliste.', // Link to put on waitlist will be for this text.
        waitlistSignupSuccess: "Danke! Du stehst jetzt auf unserer Warteliste.",
        waitlistSignupError: "Bei der Anmeldung zu unserer Warteliste ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.",
        preferredContactTimesLabel: 'Wann können wir dich am besten erreichen? (optional)',
        contactTimeMorning: "Vormittag (08:00 - 12:00 Uhr)",
        contactTimeAfternoon: "Nachmittag (12:00 - 16:00 Uhr)",
        contactTimeEvening: "Abend (16:00 - 22:00 Uhr)",
        submitWaitlistButton: "Auf Warteliste setzen",
        waitlistFormTitle: "Warteliste - Stay In Touch",
        waitlistFormSubtitle: "Wir informieren Sie umgehend per SMS und E-Mail, sobald ein früherer Termin für Ihre gewünschte Behandlung verfügbar wird.",

        personalDetails: 'Persönliche Informationen',

        firstNameLabel: 'Vorname',
        enterFirstNamePlaceholder: 'Vorname eingeben',
        firstNameRequired: 'Vorname ist erforderlich',
        firstNameHelpMessage: 'Bitte gebe einen gültigen Namen ein (2-50 Zeichen)',
        
        lastNameLabel: 'Nachname',
        enterLastNamePlaceholder: 'Nachname eingeben',
        lastNameRequired: 'Nachname ist erforderlich',
        lastNameHelpMessage: 'Bitte gebe einen gültigen Namen ein (2-50 Zeichen)',
        
        emailAddressLabel: 'Email-Adresse',
        enterEmailAddressPlaceholder: 'Email-Adresse eingeben',
        emailAddressRequired: 'Email-Adresse ist erforderlich',
        emailAddressHelpMessage: 'Bitte gebe eine gültige Email-Adresse ein',
        
        phoneNumberLabel: 'Telefonnummer',
        enterPhoneNumberPlaceholder: 'Telefonnummer eingeben',
        phoneNumberRequired: 'Telefonnummer ist erforderlich',
        phoneNumberHelpMessage: 'Bitte gebe eine gültige Telefonnummer ein',

        wantEarlierTimeslotLabel: 'Ich möchte einen früheren Termin, falls dieser verfügbar wird',

        messageLabel: 'Nachricht (optional)',
        enterMessagePlaceholder: 'Hinterlasse eine Nachricht oder stelle eine Frage...',
        // Here, {AGB's|AGB} will give you "AGB's" text with AGB link
        // and {Datenschutzbestimmungen|DATENSCHUTZ} will give you "Datenschutzbestimmungen" text with the DATENSCHUTZ link.
        iAcceptTermsAndConditions: "Ich stimme den {AGB's|AGB} und {Datenschutzbestimmungen|DATENSCHUTZ} zu.*",
        reviewBooking: 'Buchung überprüfen',
        fillOutYourInformationToContinue: 'Bitte fülle Deine Informationen aus, um fortzufahren.',

        yourTreatments: 'Deine Behandlungen',
        emptyCart: 'Dein Warenkorb ist leer',
        addTreatmentsToGetStarted: 'Füge Behandlungen hinzu, um loszulegen',

        treatmentPackagePopupTitle: 'Diese Behandlung ist als Bundle verfügbar',
        treatmentPackagePopupDescription: 'Dieses Bundleangebot ist günstiger als die Einzelbehandlung und unterstützt einen besseren Behandlungserfolg.',
        inPackage: 'im Bundle',
        perTreatment: 'pro Behandlung',
        treatmentPackageSave: 'Spare',
        toastAddedItemToCart: 'Bundle-Upgrade erfolgreich zum Warenkorb hinzugefügt!',
        upgradeToPackageDealButton: 'Bundle buchen',
        skipUpgradeToPackageDealButton: 'Überspringen',

        appointmentReview: 'Buchung überprüfen',
        confirmBooking: 'Buchung bestätigen',
        discountCodeLabel: 'Rabattcode',
        discountCodePlaceholder: 'Rabattcode eingeben',
        discountCodeCaption: 'Dieser wird persönlich in der Klinik angewendet.',
        bookingErrorTheDesiredSlotIsNotAvailableAnymore: 'Der gewünschte Termin ist nicht mehr verfügbar. Bitte wähle einen anderen Termin.',
        bookingErrorUnknown: 'Die Buchung konnte nicht erstellt werden. Wähle bitte einen anderen Termin.',
        bookingErrorInvalidSelectedData: 'Die Buchung konnte nicht erstellt werden, weil kein Termin ausgewählt wurde. Bitte gehe zurück und wähle einen Termin.',
        bookingErrorEmailBlocked: 'Eine Online-Buchung ist nicht möglich. Bitte kontaktiere die Klinik, um direkt einen Termin zu vereinbaren.',

        bookingConfirmedTitle: 'Danke für deine Buchung!',
        bookingConfirmedInstructions: 'Deine Buchung wurde erstellt. Du erhältst in Kürze eine Bestätigung per E-Mail.',

        backToBeginning: 'Zurück zum Start',

        // Login
        loginTitle: 'Willkommen zurück',
        loginSubtitle: 'Gib deine E-Mail-Adresse ein, um einen Login-Link zu erhalten',
        loginEmailPlaceholder: 'E-Mail-Adresse eingeben',
        loginSendMagicLink: 'Login-Link senden',
        loginSendingMagicLink: 'Wird gesendet...',
        loginMagicLinkSent: 'Prüfe deine E-Mails! Wir haben dir einen Login-Link gesendet.',
        loginMagicLinkError: 'Kein Konto mit dieser E-Mail-Adresse gefunden.',
        loginGenericError: 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.',
        loginBackToBooking: 'Zurück zur Buchung',
        loginVerifying: 'Login wird überprüft...',
        loginVerificationFailed: 'Login-Überprüfung fehlgeschlagen. Der Link ist möglicherweise abgelaufen.',
        loginTryAgain: 'Erneut versuchen',
        loginPromptText: 'Schon mal bei uns gewesen?',
        loginPromptLink: 'Einloggen',
        loggedInAs: 'Eingeloggt als',
        logout: 'Ausloggen',

        // Treatment Plan Card
        yourTreatmentPlan: 'Dein Treatment Plan',
        viewYourPersonalizedPlan: 'Hier zu deinen persönlichen Empfehlungen.',

        loadingYourTimeslot: 'Lade deinen Termin',
        pleaseWaitWhileWeLoadYourAvailableTimeslots: 'Bitte warte, während wir deine verfügbaren Termine laden',
        timeslotNoLongerAvailable: 'Termin nicht mehr verfügbar',
        timeslotAlreadyTakenByOtherCustomer: 'Dieser Termin wurde bereits von einem anderen Kunden gebucht.',
        checkForNewTimeslots: 'Nach neuen Terminen suchen',
        appointmentConfirmed: 'Termin bestätigt!',
        yourAppointmentHasBeenSuccessfullyBooked: 'Dein Termin wurde erfolgreich gebucht.',
        service: 'Behandlung',
        confirmationEmailSentTo: 'Eine Bestätigungs-E-Mail wurde gesendet an',
        claimYourTimeslot: 'Kurzfristiger Termin verfügbar',
        greatNewsTimeslotAvailable: 'Gute Nachrichten! Ein Termin ist verfügbar geworden.',
        bookedFor: 'Gebucht für',
        availableTimeslots: 'Freie Termine:',
        discountCode: 'Rabattcode',
        optional: 'optional',
        enterDiscountCode: 'Rabattcode eingeben',
        messageToSalon: 'Nachricht an den Salon',
        anySpecialRequests: 'Besondere Wünsche oder Fragen?',
        bookAppointment: 'Termin buchen',
        byBookingYouConfirmAvailability: 'Mit der Buchung bestätigst du, dass dieser Termin noch verfügbar ist.',
        waitlistClaimAllContactFieldsRequired: 'Alle folgenden Felder sind für die Buchung erforderlich.',
        fieldRequiredShort: '(Pflichtfeld)',
        waitlistWithdrawNotificationsLabel: 'Keine Benachrichtigungen mehr erhalten für diesen Eintrag',
        waitlistWithdrawConfirmTitle: 'Von der Warteliste abmelden?',
        waitlistWithdrawConfirmDescription:
            'Du erhältst keine E-Mails mehr zu freien Terminen für diese Anfrage. Dies kann nicht rückgängig gemacht werden.',
        waitlistWithdrawConfirmButton: 'Ja, abmelden',
        waitlistWithdrawCancelButton: 'Abbrechen',
        waitlistWithdrawSuccess: 'Du erhältst keine Benachrichtigungen mehr zu diesem Wartelisten-Eintrag.',
        waitlistWithdrawError: 'Abmeldung fehlgeschlagen. Bitte E-Mail prüfen oder erneut versuchen.',
        waitlistEntryNotAvailable: 'Dieser Wartelisten-Link ist ungültig oder nicht mehr aktiv.',
        waitlistWithdrawnTitle: 'Benachrichtigungen beendet',
        waitlistWithdrawnDescription: 'Du erhältst keine weiteren Nachrichten zu diesem Wartelisten-Eintrag.',
        waitlistLoginRequired: 'Anmeldung erforderlich',
        waitlistLoginDescription: 'Bitte melde dich an, um deine verfügbaren Termine zu sehen. Wir senden dir einen Link per E-Mail.',
        waitlistSendLoginLink: 'Login-Link senden',
        waitlistLoginLinkSent: 'Ein Login-Link wurde an deine E-Mail-Adresse gesendet. Bitte überprüfe dein Postfach.',
        waitlistLoginLinkError: 'Login-Link konnte nicht gesendet werden. Bitte versuche es erneut.',
        appointmentBookedSuccessfully: 'Termin erfolgreich gebucht!',
        appointmentBookingFailed: 'Terminbuchung fehlgeschlagen. Der Termin ist möglicherweise nicht mehr verfügbar.',
        waitlistDuplicateAppointmentTitle: 'Du hast diesen Termin bereits gebucht',
        waitlistDuplicateAppointmentWouldMoveLabel: 'Termin, der verschoben würde',
        waitlistDuplicateAppointmentNewTimeLabel: 'Neuer Zeitpunkt',
        waitlistDuplicateAppointmentDescription:
            'Du kannst diesen Termin auf die gewählte Wartezeiten-Zeit verschieben oder ihn behalten und einen weiteren Termin buchen.',
        waitlistRescheduleExistingButton: 'Bestehenden Termin verschieben',
        waitlistBookNewAppointmentButton: 'Neuen Termin buchen',
        clinic: 'Klinik',


        preferredPractitionerPlaceholder: 'Wähle deine:n Behandler:in',
        preferredPractitionerNoPreference: 'Keine Präferenz',
        preferredPractitionerInfoPast: 'Du kannst zwischen allen Behandlern wählen, bei denen du zuvor schon einmal behandelt wurdest.',
        preferredPractitionerInfoCombined: 'Bei Kombi-Terminen mit ärztlichen und kosmetischen Leistungen kannst du aktuell nur den Behandler für die ärztliche Leistung wählen.',

        deeplinkNotFound: 'Link nicht gefunden',
        deeplinkNotFoundDescription: 'Der Buchungslink ist ungültig oder abgelaufen.',
        deeplinkLocationAndServiceContinueToBookingButton: 'Weiter zur Buchung',
    },
} as const;

type TranslationKey = keyof (typeof translations)[keyof typeof translations];
type TranslationValue = (typeof translations)[keyof typeof translations][TranslationKey];

type LanguageState = {
    language: Language;
    setLanguage: (language: Language) => void;
    t: (key: TranslationKey) => TranslationValue | TranslationKey;
}

export const useLanguageStore = create<LanguageState>()(
    persist(
        (set, get) => ({
            language: Language.GERMAN, // Default to German
            setLanguage: (language: Language) => set({ language }),
            t: (key) => {
                const state = get();
                const translation = translations[state.language][key];

                if (translation === undefined) {
                    console.warn(`Translation key "${key}" not found for language ${state.language}`);
                    return key;
                }

                return translation;
            },
        }),
        {
            name: 'language-storage',
        }
    )
);
